import { type ModelContext, useAui } from '@assistant-ui/react';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { z } from 'zod';

import { usePipelineContext } from '#plugin-chat/hooks/usePipelineContext.ts';
import {
  convertTaggedToDiscriminated,
  extractDiscriminators,
  transformToTaggedObjects,
} from '#plugin-chat/lib/ai-api/tagged-object.ts';
import { datasetQueryKey } from '#plugin-chat/lib/data-pipeline/resolver/dataset-query-key.ts';
import type { PipelineContext } from '#plugin-chat/lib/data-pipeline/step/types.ts';
import { renderInstructions } from '#plugin-chat/lib/instructions/reconciler.ts';
import type {
  AnyToolConfig,
  ProducerToolConfig,
  ToolComponent,
  ToolConfig,
  ToolFactory,
  ToolFactoryWithInstructions,
} from '#plugin-chat/lib/tool/create-tool.types.ts';
import {
  ClientNotAvailableError,
  InvalidSpecError,
  ToolExecutionError,
  UnexpectedError,
} from '#plugin-chat/lib/tool/errors/index.ts';
import { registerProgressComponent } from '#plugin-chat/lib/tool/progress-registry.ts';

type ToolConfigGetter<ArgsType extends Record<string, unknown>, ResultType> =
  | (() => AnyToolConfig<ArgsType, ResultType> | undefined)
  | AnyToolConfig<ArgsType, ResultType>
  | undefined;

interface NormalizedFactoryResult<ArgsType extends Record<string, unknown>, ResultType> {
  instructions: ReactNode | undefined;
  priority: number | undefined;
  getConfig: ToolConfigGetter<ArgsType, ResultType>;
}

/** Discriminate ToolFactoryWithInstructions from a bare tool config by checking for the `config` key. */
function isFactoryWithInstructions<ArgsType extends Record<string, unknown>, ResultType>(
  result: AnyToolConfig<ArgsType, ResultType> | ToolFactoryWithInstructions<ArgsType, ResultType>,
): result is ToolFactoryWithInstructions<ArgsType, ResultType> {
  return 'config' in result;
}

/** Normalize all factory return shapes into a uniform { instructions, priority, getConfig } structure. */
function normalizeFactoryResult<ArgsType extends Record<string, unknown>, ResultType>(
  result: ReturnType<ToolFactory<unknown, ArgsType, ResultType>>,
): NormalizedFactoryResult<ArgsType, ResultType> {
  if (!result) {
    return { instructions: undefined, priority: undefined, getConfig: undefined };
  }
  if (typeof result === 'function') {
    return { instructions: undefined, priority: undefined, getConfig: result };
  }
  if (isFactoryWithInstructions(result)) {
    return { instructions: result.instructions, priority: result.priority, getConfig: result.config };
  }
  return { instructions: undefined, priority: undefined, getConfig: result };
}

const toolCache = new Map();

/**
 * Runtime guard: a dataset-producing config is identified by the presence of
 * a `produceDataset` function. Not a TypeScript type predicate — the caller
 * asserts to {@link ProducerToolConfig} at the use-site because the
 * conditional shape of {@link AnyToolConfig} makes a type-predicate form
 * non-assignable for arbitrary `ArgsType`.
 */
function isProducerConfig<ArgsType extends Record<string, unknown>, ResultType>(
  config: AnyToolConfig<ArgsType, ResultType>,
): boolean {
  return (
    typeof (config as ProducerToolConfig<ArgsType & { outputDataset: string }, ResultType>).produceDataset ===
    'function'
  );
}

/**
 * Build the wrapped `execute` for a dataset-producing tool.
 *
 * Flow on each tool call:
 *
 * 1. Convert tagged LLM args back to discriminated form (if the schema uses them).
 * 2. Build a fresh `PipelineContext` for this call.
 * 3. Call `queryClient.fetchQuery` with the canonical dataset query key —
 *    `(threadId, toolName, args)` — using `produceDataset(args, ctx)` as
 *    the `queryFn`. The TanStack cache becomes the single source of truth
 *    for the produced dataset; downstream tools cascading via
 *    `ctx.resolve.byName` dedupe against this same cache entry.
 * 4. Run `execute({ args, dataset, ctx })` to get the LLM-facing envelope.
 *
 * Post-reload materialisation does not happen here — the resolver reads
 * `produceDataset` back from `aui.modelContext().getModelContext()` and
 * fires it lazily on the first consumer lookup. See `dataset-resolver.ts`.
 *
 * `ToolExecutionError` produced by either `produceDataset` or `execute` is
 * translated to the structured response envelope the LLM sees.
 */
function buildProducerExecute<ArgsType extends Record<string, unknown>, ResultType>(
  toolName: string,
  toolDef: ProducerToolConfig<ArgsType, ResultType>,
  buildContextForExecute: () => PipelineContext,
  discriminators: Map<string, string>,
) {
  // biome-ignore lint/suspicious/noExplicitAny: Wrapper delegates to toolDef.execute with runtime-validated args
  return async (args: any) => {
    const convertedArgs =
      discriminators.size > 0 ? (convertTaggedToDiscriminated(args, discriminators) as ArgsType) : (args as ArgsType);
    console.debug('[Tool Execute] Invoking producer tool with args:', { args, convertedArgs, discriminators });
    try {
      validateConvertedArgs(toolDef.parameters, toolName, convertedArgs);
      const outputDataset = convertedArgs.outputDataset;
      if (typeof outputDataset !== 'string') {
        throw new InvalidSpecError(`Producer tool "${toolName}" called without a string outputDataset.`, {
          suggestions: [
            "Ensure the tool's parameter schema declares `outputDataset: z.string()` and the LLM supplied it.",
          ],
        });
      }
      const ctx = buildContextForExecute();
      if (!ctx.queryClient) {
        throw new ClientNotAvailableError('queryClient');
      }
      const dataset = await ctx.queryClient.fetchQuery({
        queryKey: datasetQueryKey(ctx.resolve.threadId, toolName, convertedArgs),
        queryFn: () => toolDef.produceDataset(convertedArgs, ctx),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
      });
      return await toolDef.execute({ args: convertedArgs, dataset, ctx });
    } catch (error) {
      console.error('A producer tool execution error occurred', error);
      return toExecutionErrorResponse(error, convertedArgs);
    } finally {
      // Evict only this tool's entry — the next `getModelContext` read
      // rebuilds it from the current factory config. Clearing the whole
      // module-level map would thrash unrelated tools' cached metadata.
      toolCache.delete(toolName);
    }
  };
}
/**
 * Build the wrapped `execute` for a non-producer tool. Runs tagged-arg
 * conversion, builds a fresh {@link PipelineContext} for the call, invokes
 * `toolDef.execute(args, { ctx })`, and translates any
 * `ToolExecutionError` into the structured LLM envelope.
 *
 * The pipeline context exposes the per-thread `DatasetStore`, so tools that
 * read previously-staged datasets (e.g. dashboards validating chart specs
 * against the datasets they reference) can call `ctx.store.get(key)`
 * without having to thread the store through React hooks.
 */
function buildPlainExecute<ArgsType extends Record<string, unknown>, ResultType>(
  toolName: string,
  toolDef: ToolConfig<ArgsType, ResultType>,
  buildContextForExecute: () => PipelineContext,
  discriminators: Map<string, string>,
) {
  // biome-ignore lint/suspicious/noExplicitAny: Wrapper delegates to toolDef.execute with runtime-validated args
  return async (args: any, auiCtx: any) => {
    const convertedArgs =
      discriminators.size > 0 ? (convertTaggedToDiscriminated(args, discriminators) as ArgsType) : (args as ArgsType);
    console.debug('[Tool Execute] Invoking tool with args:', { args, convertedArgs, discriminators });
    try {
      validateConvertedArgs(toolDef.parameters, toolName, convertedArgs);
      const ctx = buildContextForExecute();
      return await toolDef.execute(convertedArgs, { ...auiCtx, ctx });
    } catch (error) {
      console.error('A tool execution error occurred', error);
      return toExecutionErrorResponse(error);
    } finally {
      toolCache.clear();
    }
  };
}

/**
 * Duck-type: an error that already knows how to render itself as the
 * structured LLM envelope. The {@link ToolExecutionError} hierarchy implements
 * this contract; any future structured error class can opt in by adding the
 * same method.
 */
function hasStructuredResponse(error: unknown): error is { toStructuredResponse: () => object } {
  return (
    error instanceof ToolExecutionError ||
    (typeof error === 'object' &&
      error !== null &&
      typeof (error as { toStructuredResponse?: unknown }).toStructuredResponse === 'function')
  );
}

/**
 * Validate `convertedArgs` against the tool's parameter schema, throwing
 * {@link InvalidSpecError} on mismatch.
 *
 * Catches the silent-passthrough class of bugs in
 * `convertTaggedToDiscriminated`: when the LLM emits a tagged-format object
 * whose key isn't a registered discriminator value (e.g.
 * `{ joinDatasets: {...} }` inside a `transformDataset` actions array), the
 * conversion leaves the malformed shape in place. Without this validation
 * the malformed shape reaches `execute` / `produceDataset` and crashes with
 * a JS TypeError that gets wrapped as `UnexpectedError(recovery: fatal)`.
 *
 * Re-validating against the original (discriminated) schema after conversion
 * surfaces these mistakes as a clean Zod error formatted into actionable
 * suggestions for the LLM.
 */
function validateConvertedArgs<ArgsType extends Record<string, unknown>>(
  parameters: z.ZodType<ArgsType> | undefined,
  toolName: string,
  convertedArgs: unknown,
): void {
  if (parameters === undefined) {
    return;
  }
  const result = parameters.safeParse(convertedArgs);
  if (result.success) {
    return;
  }
  const issues = result.error.issues.slice(0, 5);
  const formattedIssues = issues.map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
    return `${path}: ${issue.message}`;
  });
  const moreCount = result.error.issues.length - issues.length;
  const overflow = moreCount > 0 ? [`(${moreCount} more validation issue${moreCount === 1 ? '' : 's'} omitted)`] : [];
  throw new InvalidSpecError(`Tool "${toolName}" received malformed arguments after tagged-union conversion.`, {
    suggestions: [
      ...formattedIssues,
      ...overflow,
      "Verify each field matches the tool's declared schema. For union-typed fields (e.g. action arrays), use the documented `type` discriminator.",
    ],
    details: {
      toolName,
      issues: result.error.issues.map((issue) => ({
        path: issue.path,
        code: issue.code,
        message: issue.message,
      })),
    },
  });
}

/** Translate an error thrown from `execute` / `produceDataset` into the structured LLM envelope. */
function toExecutionErrorResponse(error: unknown, _args?: object) {
  if (hasStructuredResponse(error)) {
    return error.toStructuredResponse();
  }
  return new UnexpectedError(error instanceof Error ? error.message : String(error)).toStructuredResponse();
}

export function createTool<
  const PropsType,
  const ArgsType extends Record<string, unknown> = Record<string, unknown>,
  const ResultType = unknown,
>(toolName: string, toolFactory: ToolFactory<PropsType, ArgsType, ResultType>): ToolComponent<PropsType> {
  function ToolImpl(props: PropsType) {
    const aui = useAui();
    const buildPipelineContext = usePipelineContext();
    const factoryResult = toolFactory(props);

    const { instructions, getConfig } = normalizeFactoryResult(factoryResult);

    // "Latest value" refs: stable identity across renders, but always read the
    // current render's closures when dereferenced. Needed because the mount
    // effect (deps: [aui, instructions]) does not re-run when the thread
    // changes or the tool factory re-runs, yet the producer-tool execute
    // wrapper must observe the current pipeline context (current thread's
    // query client + resolver, latest accessors).
    const buildPipelineContextRef = useRef(buildPipelineContext);
    const getConfigRef = useRef(getConfig);
    useEffect(() => {
      buildPipelineContextRef.current = buildPipelineContext;
      getConfigRef.current = getConfig;
    }, [buildPipelineContext, getConfig]);

    useEffect(() => {
      const resolveConfig = () =>
        typeof getConfigRef.current === 'function' ? getConfigRef.current() : getConfigRef.current;
      const getContext = () => buildPipelineContextRef.current().context;

      const toolDef = resolveConfig();
      console.debug(`[Tool] Registering tool: ${toolName}`, { tool: toolDef });

      // Gate: if toolDef is undefined, don't register the tool (gated tool pattern)
      if (!toolDef) {
        console.debug(`[Tool] Tool gated (not registering): ${toolName}`);
        return;
      }

      // Bidirectional tagged-object conversion:
      // Outbound: discriminated unions in JSON schema -> tagged format (for LLM compatibility)
      // Inbound: tagged LLM output -> discriminated format (for app consumption)
      const discriminators = toolDef.parameters ? extractDiscriminators(toolDef.parameters) : new Map<string, string>();

      const getModelContext = (): ModelContext => {
        // console.debug(`[Tool ModelContext] Building model context for tool: ${toolName}`);
        if (toolCache.has(toolName)) {
          // console.debug(`[Tool ModelContext] Returning cached model context for tool: ${toolName}`);
          return toolCache.get(toolName);
        }

        const toolDef = resolveConfig();
        if (!toolDef) {
          console.debug(`[Tool ModelContext] Tool gated (not registering): ${toolName}`);
          return { tools: {} };
        }

        const renderedDescription = renderInstructions(instructions);

        // Producer tools: the framework owns produce-then-stage-then-execute.
        // Non-producer tools: pass args straight through to execute. The cast
        // narrows the AnyToolConfig union based on the runtime guard.
        const wrappedExecute = isProducerConfig(toolDef)
          ? buildProducerExecute(
              toolName,
              toolDef as ProducerToolConfig<ArgsType & { outputDataset: string }, ResultType>,
              getContext,
              discriminators,
            )
          : buildPlainExecute(toolName, toolDef as ToolConfig<ArgsType, ResultType>, getContext, discriminators);

        // Attach `produceDataset` directly to the tool entry for producer tools.
        // The assistant-ui runtime only reads `description` / `parameters` /
        // `execute` on the LLM path, but keeps the full object accessible via
        // `aui.modelContext().getModelContext().tools[name]`. The dataset
        // resolver reads `produceDataset` back through that API so cascades
        // and `useDataset` can materialise datasets post-reload without
        // depending on the tool-call render tree mounting first.
        const tool = {
          description: renderedDescription,
          parameters: toolDef.parameters
            ? transformToTaggedObjects(toolDef.parameters.toJSONSchema({ unrepresentable: 'any' }))
            : undefined,
          execute: wrappedExecute,
          ...(isProducerConfig(toolDef) && {
            produceDataset: (args, ctx) => {
              const convertedArgs =
                discriminators.size > 0
                  ? (convertTaggedToDiscriminated(args, discriminators) as ArgsType)
                  : (args as ArgsType);
              return (toolDef as ProducerToolConfig<ArgsType, ResultType>).produceDataset(convertedArgs, ctx);
            },
          }),
        };

        const ret: ModelContext = { tools: { [toolName]: tool } };
        toolCache.set(toolName, ret);
        return ret;
      };

      // Render: both config variants share the `render` field from ToolConfigBase,
      // so access is uniform. Tagged-object converter re-maps args only when the
      // schema actually uses discriminated unions.
      // biome-ignore lint/suspicious/noExplicitAny: render prop shape varies per tool; the wrapper only re-maps args
      const renderFn = toolDef.render as ((props: any) => ReactNode) | undefined;
      const userRender =
        renderFn && discriminators.size > 0
          ? // biome-ignore lint/suspicious/noExplicitAny: discriminator re-mapping widens the props type to unknown args
            (props: { args: any }) => {
              // console.log(
              //   `[Tool Render] Rendering tool: ${toolName} with props:`,
              //   convertTaggedToDiscriminated(props.args, discriminators),
              //   discriminators,
              // );
              return renderFn({ ...props, args: convertTaggedToDiscriminated(props.args, discriminators) });
            }
          : renderFn;

      // Producer tools expose `produceDataset` via the model context (set on
      // the tool entry in `getModelContext` above). The dataset resolver
      // reads it back by tool name, so there's no render-time cache seeding
      // — `useDataset` and cascades work regardless of whether the tool-call
      // UI has mounted.
      // biome-ignore lint/suspicious/noExplicitAny: setToolUI accepts a render function with any props shape
      const unsetToolUi = aui.tools().setToolUI(toolName, (userRender ?? (() => null)) as any);

      // A tool's `progress` declaration is the signal that it's collapsible.
      // Registering here (alongside setToolUI) keeps the lifecycle in sync.
      const noopUnregister = () => undefined;
      const unregisterProgress = toolDef.progress
        ? registerProgressComponent(toolName, toolDef.progress)
        : noopUnregister;
      const unregisterToolEvent = aui.modelContext().register({ getModelContext });

      return () => {
        console.debug(`[Tool] Unregistering tool: ${toolName}`);
        // Evict only this tool's memoised model-context entry. Other tools
        // keep their cached metadata.
        toolCache.delete(toolName);
        unregisterToolEvent();
        unregisterProgress();
        unsetToolUi();
      };
    }, [aui, instructions]);

    return null;
  }

  ToolImpl.displayName = `Tool(${toolName})`;
  ToolImpl.toString = () => toolName;
  ToolImpl.mention = `\`${toolName}\``;

  return ToolImpl;
}
